/**
 * Reporting a message (F6): the seven-word reason vocabulary is the
 * client's (echo_app::legal::REPORT_REASONS — the server checks only length,
 * the words are a UI contract), the comment is capped at the server's 1000.
 */
import { For, type JSX, createSignal } from "solid-js";

import { currentLifetime, isCurrent } from "../lib/lifetime";
import * as api from "../lib/api/rest";
import type { Dict } from "../lib/i18n";
import type { Runtime } from "../store/runtime";
import { Button } from "./ds";

const REPORT_REASONS = ["abuse", "sexual", "child", "violence", "illegal", "spam", "other"];
const MAX_COMMENT = 1000;

export function ReportDialog(props: {
  rt: Runtime;
  t: Dict;
  channelId: number;
  seq: number;
  onClose(): void;
}): JSX.Element {
  const t = props.t;
  const [reason, setReason] = createSignal("abuse");
  const [comment, setComment] = createSignal("");
  const [busy, setBusy] = createSignal(false);

  async function send(): Promise<void> {
    const lifetime = currentLifetime();
    if (busy()) return;
    setBusy(true);
    try {
      await api.reportMessage(props.channelId, props.seq, reason(), comment().slice(0, MAX_COMMENT));
      if (!isCurrent(lifetime)) return;
      props.rt.showNotice(t.reportSent());
      props.onClose();
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      props.rt.showNotice(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  // No .echo-transition-enter on this scrim: .report-card centers by transform
  // (web-compat.css), and the fallback rise keyframe would stomp it. The
  // view transition (swap "sheet") animates the snapshot instead.
  return (
    <div class="echo-scrim" onClick={() => props.onClose()}>
      <div style="display:contents" onClick={(e) => e.stopPropagation()}>
        <div class="profile-card echo-material-thick report-card">
          <span class="report-card__title">{t.reportTitle()}</span>
          <div class="report-card__reasons">
            <For each={REPORT_REASONS}>
              {(key) => (
                <button
                  type="button"
                  class={`report-card__reason ${reason() === key ? "report-card__reason--on" : ""}`}
                  onClick={() => setReason(key)}
                >
                  {t.reportReasonLabel(key)}
                </button>
              )}
            </For>
          </div>
          <textarea
            class="hotkey-capture report-card__comment"
            placeholder={t.reportCommentPlaceholder()}
            maxLength={MAX_COMMENT}
            value={comment()}
            onInput={(e) => setComment(e.currentTarget.value)}
          />
          <div class="report-card__row">
            <span class="layout-spacer" />
            <Button icon="close" kind="bordered" size="medium" onClick={() => props.onClose()}>
              {t.cardDecline()}
            </Button>
            <Button icon="report_problem" kind="prominent" size="medium" disabled={busy()} onClick={() => void send()}>
              {t.reportSend()}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
